import React, { useState, useEffect } from 'react';
import { Flex, Typography, Box, SelectDropdown, Card, IconButton } from '@kistech/kis-ui';
import { ConfigurationsProps } from '../../types';
import get from 'lodash.get';
import { useStyles, Tooltips } from '../../styled';
import { ConfigEditDrawer } from '../ConfigEditDrawer';
import {
  Table as MuiTable,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box as MuiBox,
  Typography as MuiTypography,
  Collapse
} from '@mui/material';
import { getFormattedValue, TemplateTypeList, HardwareTypeList, formatConfigTitle } from '../../utils';
import { ActionOverlay } from 'components/ActionOverlay';

export const Configurations = (props: ConfigurationsProps) => {
  const { configData, configType, columns, onDeleteConfirm, setDrawerVisibility } = props;
  const columnKeys = columns.reduce((acc: string[], cur) => {
    acc.push(cur.key);
    return acc;
  }, []);
  const dropDownColumns =
    configData &&
    Object.keys(configData)
      .filter((column) => columnKeys.includes(column))
      .sort();

  const [selectedColumn, setSelectedColumn] = useState<string>(dropDownColumns ? dropDownColumns[0] : '');
  const [selectedProperty, setSelectedProperty] = useState<string>('');
  const [selectedSubProperty, setSelectedSubProperty] = useState<string>('');
  const [selectedDataType, setSelectedDataType] = useState<string>('string');
  const [selectedPropertyValue, setSelectedPropertyValue] = useState<boolean | number | string | object>('');
  const [showDrawer, setShowDrawer] = useState(false);
  const [showAddConfig, setShowAddConfig] = useState(false);
  const [addConfigDropdown, setAddConfigDropdown] = useState<typeof columns>([]);
  const [showDeleteOverlay, setShowDeleteOverlay] = useState(false);
  const [expandedRows, setExpandedRows] = useState<Record<number, boolean>>({});
  const classes = useStyles({ showDrawer })();
  const filtered = get({ ...configData }, selectedColumn);

  useEffect(() => {
    const selectedDropDown = columns.find((item) => item.key === selectedColumn && item.type === 'object');
    let addMoreConfigCount = 0;
    if (selectedDropDown && selectedDropDown.children) {
      addMoreConfigCount = selectedDropDown.children
        .reduce((acc: string[], cur) => {
          acc.push(cur.key);
          return acc;
        }, [])
        .filter((column) => !Object.keys(filtered).includes(column)).length;
    }
    setShowAddConfig(addMoreConfigCount > 0);
    setExpandedRows({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedColumn]);

  const getFormattedItem = (column: string) => {
    const columnItem = columns.find((item) => item.key === column);
    if (columnItem && columnItem.type) {
      return formatConfigTitle(column, columnItem.type);
    }

    return column;
  };

  const getSelectedType = (selectedCol: string, property: string, subProperty?: string): string => {
    let selected = columns.find((column) => column.key === selectedCol);
    if (subProperty && selected && selected.children) {
      selected = selected.children.find((column) => column.key === subProperty);
    }
    if (property !== selectedCol && selected && selected.children) {
      selected = selected.children.find((column) => column.key === property);
    }
    return selected ? selected.type : 'object';
  };

  const hasChildrenDefined = (selectedCol: string, property: string): boolean => {
    let hasChildren = false;
    const selected = columns.find((cl) => cl.key === selectedCol);
    if (selected && selected.children && selected.children.length) {
      const selectedChildren = selected.children.find((chld) => chld.key === property);
      if (selectedChildren && selectedChildren.children && selectedChildren.children.length) {
        hasChildren = true;
      }
    }

    return hasChildren;
  };

  const setEditDrawerVisibility = (status: boolean) => {
    setShowDrawer(status);
    setDrawerVisibility(status);
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const onEditClick = (selectedCol: string, property: string, value: any, subProperty?: string) => {
    setSelectedDataType(getSelectedType(selectedCol, property, subProperty));
    setSelectedProperty(property);
    setSelectedSubProperty(subProperty || '');
    setAddConfigDropdown([]);
    setSelectedPropertyValue(value === null || value === undefined ? '' : value);
    setSelectedColumn(selectedCol);
    setEditDrawerVisibility(true);
  };

  const getValidDropdownOptions = (selectedCol: string, subProperty?: string): typeof columns => {
    let dropDownOptions: typeof columns = [];
    const selected = columns.find((item) => item.key === selectedCol && item.type === 'object');
    if (selected && selected.children) {
      if (subProperty) {
        const selectedChildren = selected.children.find((chld) => chld.key === subProperty && chld.type === 'object');
        if (selectedChildren && selectedChildren.children) {
          dropDownOptions = selectedChildren.children.filter(
            (item) => filtered[subProperty] && !Object.keys(filtered[subProperty]).includes(item.key)
          );
        }
      } else {
        dropDownOptions = selected.children.filter((item) => !Object.keys(filtered).includes(item.key));
      }
    }

    return dropDownOptions;
  };

  const onAddConfigClick = (selectedCol: string, subProperty?: string) => {
    const dropDownOptions = getValidDropdownOptions(selectedCol, subProperty);
    setAddConfigDropdown(dropDownOptions);
    setSelectedPropertyValue('');
    setSelectedSubProperty(subProperty || '');
    setSelectedColumn(selectedColumn);
    setEditDrawerVisibility(true);
  };

  const onDeleteConfigClick = (key: string, subProperty?: string) => {
    setSelectedProperty(key);
    setSelectedSubProperty(subProperty || '');
    setShowDeleteOverlay(true);
  };

  const onConfigDeleteConfirm = () => {
    let payload = {};
    const name = selectedProperty;
    if (selectedSubProperty) {
      const subName = selectedSubProperty;
      const { [subName]: removedProperty, ...remainingProperty } = filtered[selectedProperty];
      payload = {
        id: configData && configData.id,
        [selectedColumn]: { ...filtered, [name]: remainingProperty }
      };
    } else {
      const { [name]: removedProperty, ...remainingPayload } = filtered;
      payload = {
        id: configData && configData.id,
        [selectedColumn]: remainingPayload
      };
    }
    onDeleteConfirm(configType, payload);
    setShowDeleteOverlay(false);
  };

  const resetOverlay = () => {
    setEditDrawerVisibility(false);
    setShowDeleteOverlay(false);
    setSelectedProperty('');
    setSelectedPropertyValue('');
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const getSubRow = (value: any, key: string) => {
    return Object.keys(value)
      .sort()
      .map((property) => (
        <TableRow className={classes.configTableSubRows}>
          <TableCell className={classes.configSubPropertyCell}>
            <Typography size={16}>{property}</Typography>
          </TableCell>
          <TableCell className={classes.configTableSubValueCell}>
            <Typography size={16}>{getFormattedValue(value[property])}</Typography>
          </TableCell>
          <TableCell className={classes.configSubActionCell}>
            <Flex align="stretch" fullWidth justify="end">
              <IconButton
                name="exit_circle"
                onClick={() => onDeleteConfigClick(key, property)}
                data-testid="delete-config-button"
                style={{ marginRight: '10px' }}
              />
              <IconButton
                name="edit_circle"
                onClick={() => onEditClick(selectedColumn, property, value[property], key)}
                data-testid="update-user-button"
              />
            </Flex>
          </TableCell>
        </TableRow>
      ));
  };

  const ConfigTableRow = (rowProps: {
    idx?: number;
    property: string;
    value: string | number;
    editAction: () => void;
    deleteAction?: () => void;
  }) => {
    const { idx = 0, property, value } = rowProps;
    const hasChildren = hasChildrenDefined(selectedColumn, property);
    const isExpand = expandedRows[idx];
    const dropDownOptions = getValidDropdownOptions(selectedColumn, property);
    const handleClick = () => {
      if (hasChildren) {
        setExpandedRows({ ...expandedRows, [idx]: isExpand ? false : true });
      }
    };

    return (
      <>
        <TableRow
          className={classes.configTableRows}
          onClick={handleClick}
          style={hasChildren ? { cursor: 'pointer' } : {}}
        >
          <TableCell className={classes.configPropertyCell}>
            <Typography size={16}>{property}</Typography>
          </TableCell>
          <TableCell className={classes.configTableValueCell}>
            {!isExpand && <Typography size={16}>{getFormattedValue(value)}</Typography>}
          </TableCell>
          <TableCell className={classes.configActionCell}>
            <Flex align="stretch" fullWidth justify="end">
              {isExpand && dropDownOptions.length > 0 && (
                <IconButton
                  name="plus_circle"
                  onClick={(e) => {
                    e.stopPropagation();
                    onAddConfigClick(selectedColumn, property);
                  }}
                  data-testid="delete-config-button"
                  style={{ marginRight: '10px' }}
                />
              )}
              {rowProps.deleteAction && (
                <IconButton
                  name="exit_circle"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (rowProps.deleteAction) rowProps.deleteAction();
                  }}
                  data-testid="delete-config-button"
                  style={{ marginRight: '10px' }}
                />
              )}
              <IconButton
                name="edit_circle"
                onClick={(e) => {
                  e.stopPropagation();
                  rowProps.editAction();
                }}
                data-testid="update-user-button"
              />
            </Flex>
          </TableCell>
        </TableRow>
        <TableRow className={classes.tableSubRows} style={isExpand ? { marginBottom: '10px' } : { display: 'none' }}>
          <TableCell style={{ padding: '0px', flex: '1', borderRadius: '8px' }} colSpan={3}>
            <Collapse in={isExpand} timeout="auto" unmountOnExit>
              {isExpand ? (
                <MuiTable size="small" aria-label="cart-sub-table" className={classes.subTable}>
                  <TableBody>{getSubRow(value as string, property)}</TableBody>
                </MuiTable>
              ) : null}
            </Collapse>
          </TableCell>
        </TableRow>
      </>
    );
  };

  return (
    <>
      <Flex direction="column" flexOverride="1 1 auto" maxWidth="100%">
        <Flex direction="row" margin="0 6 6" align="center">
          <Typography size={24} weight={700}>
            Category
          </Typography>
          <Box width={20} />
          <SelectDropdown
            items={dropDownColumns ? dropDownColumns : ['']}
            itemToString={getFormattedItem}
            selectedItem={selectedColumn}
            onChange={(column: string) => setSelectedColumn(column)}
            placeholder="Select a category"
            data-testid="media-selector"
          />
        </Flex>
        {/* TABLE */}
        <MuiBox className={classes.configTableOuterBox}>
          <Card hasBorderRadius raised fullHeight>
            <TableContainer id="userViewTable" className={classes.configTableContainer}>
              <MuiTable stickyHeader style={{ borderCollapse: 'collapse', width: '100%' }}>
                <TableHead>
                  <TableRow className={classes.configTableRows}>
                    <TableCell className={classes.configHeadersProperty}>Property</TableCell>
                    <TableCell className={classes.configTableHeaders}>Value</TableCell>
                    <TableCell className={classes.configActionHeader}>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filtered && getSelectedType(selectedColumn, selectedColumn) === 'object'
                    ? Object.keys(filtered)
                        .sort()
                        .map((property, idx) => (
                          <ConfigTableRow
                            key={idx}
                            idx={idx}
                            property={property}
                            value={filtered[property]}
                            editAction={() => onEditClick(selectedColumn, property, filtered[property])}
                            deleteAction={() => onDeleteConfigClick(property)}
                          />
                        ))
                    : null}
                  {['configuration', 'clientIntegration', 'venue', 'area', 'integration'].includes(
                    getSelectedType(selectedColumn, selectedColumn)
                  ) && (
                    <ConfigTableRow
                      property={formatConfigTitle(selectedColumn, getSelectedType(selectedColumn, selectedColumn))}
                      value={filtered.name}
                      editAction={() => onEditClick(selectedColumn, selectedColumn, filtered)}
                    />
                  )}
                  {['templateType'].includes(getSelectedType(selectedColumn, selectedColumn)) && (
                    <ConfigTableRow
                      property={formatConfigTitle(selectedColumn, getSelectedType(selectedColumn, selectedColumn))}
                      value={filtered}
                      editAction={() =>
                        onEditClick(
                          selectedColumn,
                          selectedColumn,
                          TemplateTypeList.find((t) => t.code === filtered)
                        )
                      }
                    />
                  )}
                  {['hardwareType'].includes(getSelectedType(selectedColumn, selectedColumn)) && (
                    <ConfigTableRow
                      property={formatConfigTitle(selectedColumn)}
                      value={filtered}
                      editAction={() =>
                        onEditClick(
                          selectedColumn,
                          selectedColumn,
                          HardwareTypeList.find((t) => t.code === filtered)
                        )
                      }
                    />
                  )}
                  {![
                    'object',
                    'configuration',
                    'clientIntegration',
                    'venue',
                    'templateType',
                    'area',
                    'integration',
                    'hardwareType'
                  ].includes(getSelectedType(selectedColumn, selectedColumn)) && (
                    <ConfigTableRow
                      property={selectedColumn}
                      value={filtered}
                      editAction={() => onEditClick(selectedColumn, selectedColumn, filtered)}
                    />
                  )}
                </TableBody>
              </MuiTable>
            </TableContainer>
            {showAddConfig && getValidDropdownOptions(selectedColumn).length > 0 && (
              <MuiBox className={classes.addConfigRow}>
                <Tooltips title="Add Config" arrow placement={'top'}>
                  <IconButton name="plus_circle" onClick={() => onAddConfigClick(selectedColumn)} />
                </Tooltips>
              </MuiBox>
            )}
          </Card>
        </MuiBox>
      </Flex>

      {showDrawer && (
        <ConfigEditDrawer
          onClose={resetOverlay}
          isOpen={showDrawer}
          configData={configData}
          selectedColumn={selectedColumn}
          selectedColumnValue={filtered}
          selectedProperty={selectedProperty}
          selectedPropertyValue={selectedPropertyValue}
          selectedSubProperty={selectedSubProperty}
          selectedDataType={selectedDataType}
          configType={configType}
          addConfigDropdown={addConfigDropdown}
        />
      )}

      {showDeleteOverlay && (
        <ActionOverlay
          title={
            <MuiBox className={classes.configDeleteHeading}>
              <MuiTypography className={classes.configDeleteHeader} style={{ lineHeight: 1, fontWeight: 600 }}>
                Are you sure you want to delete {selectedSubProperty ? selectedSubProperty : selectedProperty}?
              </MuiTypography>
            </MuiBox>
          }
          confirmText="Delete"
          onConfirm={() => onConfigDeleteConfirm()}
          cancelText="Back"
          onCancel={resetOverlay}
          width={800}
          appearance="error"
        />
      )}
    </>
  );
};
